/**
 * PostgreSQL Dialect Adapter
 *
 * Implements dialect-specific SQL generation for PostgreSQL databases.
 * Uses PostgreSQL's native JSONB operators for JSON operations.
 */
import {
  assertPortableScalarValueType,
  type PortableCountDistinctValueType,
} from "../aggregate-value-types";
import { type ValueType } from "../ast";
import { type JsonPointer, parseJsonPointer } from "../json-pointer";
import { sql, type SqlFragment } from "../sql-fragment";
import { applyAggregateFilter } from "./aggregate-filter";
import { tsvectorStrategy } from "./fulltext-strategy";
import { likeEscapeClause } from "./like-escape";
import { DOUBLE_OVERFLOW_BOUNDARY } from "./numeric-conversion";
import {
  getSqlDialectProfile,
  inlineSqlStringLiteral,
  packSqlListValue,
} from "./profile";
import { type DialectAdapter } from "./types";

const SCALAR_SQL_TYPES: Readonly<
  Record<PortableCountDistinctValueType, string>
> = {
  boolean: "boolean",
  date: "timestamptz",
  number: "numeric",
  string: "text",
};

/** Gives expression membership a concrete PostgreSQL JSON scalar type. */
function postgresJsonScalar(
  value: SqlFragment,
  valueType: ValueType,
): SqlFragment {
  switch (valueType) {
    case "boolean": {
      return sql`CAST(${value} AS boolean)`;
    }
    case "date": {
      // Date arrays are serialized with Date#toJSON(), whose UTC millisecond
      // form differs from PostgreSQL's ordinary timestamptz text rendering.
      return sql`to_char(CAST(${value} AS timestamptz) AT TIME ZONE 'UTC', 'YYYY-MM-DD"T"HH24:MI:SS.MS"Z"')`;
    }
    case "number": {
      return sql`CAST(${value} AS numeric)`;
    }
    case "string": {
      return sql`CAST(${value} AS text)`;
    }
    case "array":
    case "embedding":
    case "object":
    case "unknown": {
      throw new Error(
        `JSON array expression membership requires a scalar element type, got ${valueType}`,
      );
    }
  }
}

function buildTextJsonArray(values: readonly SqlFragment[]): SqlFragment {
  return sql`jsonb_build_array(${sql.join(
    values.map((value) => sql`CAST(${value} AS text)`),
    sql`, `,
  )})`;
}

// Exact round-to-nearest boundaries for IEEE-754 binary64. Keeping these as
// decimal NUMERIC literals lets PostgreSQL decide whether a text value can be
// converted before a DOUBLE PRECISION cast has a chance to throw.
const DOUBLE_ZERO_ROUNDING_BOUNDARY = `${5n ** 1075n}e-1075`;

/**
 * Escapes a string for use in a PostgreSQL string literal, independent of
 * server configuration.
 *
 * A plain `'…'` literal is only safe when the value contains no backslash:
 * under the legacy `standard_conforming_strings = off` setting, backslashes
 * act as escape characters inside regular literals, so a value ending in
 * `\` could swallow the closing quote and change how the statement parses.
 * Values containing a backslash therefore use the `E'…'` form — where
 * backslash is an escape character under BOTH settings — with backslashes
 * and quotes doubled. Backslash-free values keep the plain form so the
 * emitted SQL text (which callers rely on being identical across clauses)
 * is unchanged for the common case.
 */
/**
 * Converts a JSON pointer to PostgreSQL's text array path.
 *
 * Uses raw SQL (non-parameterized) to ensure the same expression text
 * is generated when the same field is used in multiple clauses (SELECT, GROUP BY).
 * Pointers usually come from schema definitions, but some (e.g. a weighted
 * traversal's `weightProperty`) are runtime strings — safe either way
 * because the shared PostgreSQL literal renderer escapes independently of server
 * configuration.
 *
 * @example
 * "/name" → ARRAY['name']
 * "/items/0" → ARRAY['items', '0']
 * "/a/b/c" → ARRAY['a', 'b', 'c']
 */
function toPostgresPath(pointer: JsonPointer): SqlFragment {
  if (!pointer || pointer === "" || pointer === "/") {
    return sql.raw("ARRAY[]::text[]");
  }

  const segments = parseJsonPointer(pointer);
  if (segments.length === 0) {
    return sql.raw("ARRAY[]::text[]");
  }

  // Use raw SQL for path segments to ensure identical SQL text
  // when the same field is used in multiple clauses (e.g., SELECT and GROUP BY)
  const escapedSegments = segments
    .map((segment) => inlineSqlStringLiteral(segment, "postgres"))
    .join(", ");
  return sql.raw(`ARRAY[${escapedSegments}]`);
}

/**
 * The cast that turns a text element of a list-valued `IN` parameter into the
 * type its left operand was extracted as. Mirrors the `jsonExtract*` casts:
 * `jsonExtractNumber` yields numeric, `jsonExtractBoolean` boolean,
 * `jsonExtractDate` timestamptz, and everything else stays text.
 */
function postgresElementCast(elementType: ValueType | undefined): SqlFragment {
  switch (elementType) {
    case "number": {
      return sql.raw("::numeric");
    }
    case "boolean": {
      return sql.raw("::boolean");
    }
    case "date": {
      return sql.raw("::timestamptz");
    }
    // `jsonExtractText` leaves these as text, so the elements must stay text
    // too. The compiler rejects array/object/embedding before reaching here.
    case "string":
    case "array":
    case "object":
    case "embedding":
    case "unknown":
    case undefined: {
      return sql.empty();
    }
  }
}

/**
 * PostgreSQL dialect adapter implementation.
 */
export const postgresDialect: DialectAdapter = {
  safeNumericConversion(expression) {
    const trimmed = sql`btrim(${expression}, ${" \t\r\n"})`;
    const exact = sql`CAST(${trimmed} AS NUMERIC)`;
    const converted = sql`CAST(${trimmed} AS DOUBLE PRECISION)`;
    const signedZero = sql`CASE WHEN left(${trimmed}, 1) = '-' THEN CAST('-0' AS DOUBLE PRECISION) ELSE CAST('0' AS DOUBLE PRECISION) END`;
    return sql`CASE WHEN length(${trimmed}) <= 400 AND ${trimmed} ~ '^-?(?:0|[1-9][0-9]*)(?:\\.[0-9]+)?(?:[eE][+-]?[0-9]{1,3})?$' THEN CASE WHEN abs(${exact}) >= ${sql.raw(DOUBLE_OVERFLOW_BOUNDARY)}::numeric THEN NULL WHEN abs(${exact}) <= ${sql.raw(DOUBLE_ZERO_ROUNDING_BOUNDARY)}::numeric THEN ${signedZero} ELSE ${converted} END ELSE NULL END`;
  },
  name: "postgres",
  capabilities: {
    standardQueryStrategy: "cte_project",
    recursiveQueryStrategy: "recursive_cte",
    materializeIntermediateTraversalCtes: false,
    emitNotMaterializedHint: true,
    forceRecursiveWorktableOuterJoinOrder: false,
    vectorPredicateStrategy: "native",
    vectorMetrics: ["cosine", "l2", "inner_product"] as const,
    supportsFulltext: true,
    subgraphMembershipStrategy: "materialized-ids",
  },

  binaryText(expression) {
    return sql`${expression} COLLATE "C"`;
  },

  analyzeTemporaryTable(table) {
    return sql`ANALYZE ${table}`;
  },

  setTransactionWorkingMemory(workingMemory) {
    // The parameterizable form of `SET LOCAL work_mem`: is_local => true
    // scopes the override to the current transaction, matching the pgvector
    // iterative-scan GUC handling elsewhere in the backend.
    return sql`SELECT set_config('work_mem', ${workingMemory}, true)`;
  },

  orderedRowsJsonArray(rowAlias, _columns, orderColumn) {
    const row = sql.identifier(rowAlias);
    const order = sql`${row}.${sql.identifier(orderColumn)}`;
    return sql`(SELECT COALESCE(jsonb_agg(to_jsonb(${row}) - ${orderColumn} ORDER BY ${order}), '[]'::jsonb) FROM ${row})`;
  },

  orderedScalarJsonArray({ filter, orderBy, value, valueType }) {
    assertPortableScalarValueType(valueType, "COLLECT");
    // Bind-only operands have no SQL context from which PostgreSQL can infer a type.
    const typedValue = sql`CAST(${value} AS ${sql.raw(SCALAR_SQL_TYPES[valueType])})`;
    const aggregate = sql`jsonb_agg(to_jsonb(${typedValue}) ORDER BY ${sql.join(orderBy, sql`, `)})`;
    const filteredAggregate = applyAggregateFilter(aggregate, filter);
    return sql`COALESCE(${filteredAggregate}, '[]'::jsonb)`;
  },

  orderedRecordJsonArray({ fields, filter, orderBy }) {
    const parts = Array.from(
      { length: Math.ceil(fields.length / 40) },
      (_, index) => {
        const chunk = fields.slice(index * 40, (index + 1) * 40);
        const pairs = chunk.flatMap((field) => {
          assertPortableScalarValueType(
            field.valueType,
            "COLLECT record field",
          );
          return [
            sql`CAST(${field.name} AS text)`,
            sql`CAST(${field.value} AS ${sql.raw(SCALAR_SQL_TYPES[field.valueType])})`,
          ];
        });
        return sql`jsonb_build_object(${sql.join(pairs, sql`, `)})`;
      },
    );
    const record = sql.join(parts, sql` || `);
    const aggregate = sql`jsonb_agg(${record} ORDER BY ${sql.join(orderBy, sql`, `)})`;
    return sql`COALESCE(${applyAggregateFilter(aggregate, filter)}, '[]'::jsonb)`;
  },

  // ============================================================
  // JSON Path Operations
  // ============================================================

  compilePath(pointer) {
    return toPostgresPath(pointer);
  },

  jsonExtract(column, pointer) {
    // #> returns JSONB value at path
    const path = toPostgresPath(pointer);
    return sql`${column} #> ${path}`;
  },

  jsonExtractText(column, pointer) {
    // #>> returns text value at path
    const path = toPostgresPath(pointer);
    return sql`${column} #>> ${path}`;
  },

  jsonExtractNumber(column, pointer) {
    // Extract as text then cast to numeric
    const path = toPostgresPath(pointer);
    return sql`(${column} #>> ${path})::numeric`;
  },

  jsonExtractDouble(column, pointer) {
    // float8, not ::numeric — decimal arithmetic would diverge from
    // SQLite's binary doubles when values accumulate.
    const path = toPostgresPath(pointer);
    return sql`(${column} #>> ${path})::double precision`;
  },

  jsonExtractBoolean(column, pointer) {
    // Extract as text then cast to boolean
    const path = toPostgresPath(pointer);
    return sql`(${column} #>> ${path})::boolean`;
  },

  jsonExtractDate(column, pointer) {
    // Extract as text then cast to timestamptz
    const path = toPostgresPath(pointer);
    return sql`(${column} #>> ${path})::timestamptz`;
  },

  // ============================================================
  // JSON Array Operations
  // ============================================================

  jsonArrayLength(column) {
    return sql`jsonb_array_length(${column})`;
  },

  jsonArrayContains(column, value) {
    // @> checks if left contains right
    const jsonValue = JSON.stringify([value]);
    return sql`${column} @> ${jsonValue}::jsonb`;
  },

  jsonArrayContainsExpression(column, value, valueType) {
    const scalar = postgresJsonScalar(value, valueType);
    return sql`CASE WHEN jsonb_typeof(${column}) = 'array' THEN EXISTS (SELECT 1 FROM jsonb_array_elements(${column}) AS tg_element(value) WHERE tg_element.value = to_jsonb(${scalar})) ELSE FALSE END`;
  },

  rowValueComparison(operator, left, right) {
    return sql`(${sql.join(left, sql`, `)}) ${sql.raw(operator)} (${sql.join(right, sql`, `)})`;
  },

  jsonArrayContainsAll(column, values) {
    if (values.length === 0) {
      return sql.raw("1=1");
    }

    // @> with full array checks all values
    const jsonValue = JSON.stringify(values);
    return sql`${column} @> ${jsonValue}::jsonb`;
  },

  jsonArrayContainsAny(column, values) {
    if (values.length === 0) {
      return sql.raw("1=0");
    }

    // Check each value with @> and OR them together
    // PostgreSQL doesn't have a native "overlaps" for jsonb arrays
    const conditions = values.map((value) => {
      const jsonValue = JSON.stringify([value]);
      return sql`${column} @> ${jsonValue}::jsonb`;
    });
    return sql`(${sql.join(conditions, sql` OR `)})`;
  },

  // ============================================================
  // JSON Object Operations
  // ============================================================

  jsonHasPath(column, pointer) {
    const path = toPostgresPath(pointer);
    return sql`${column} #> ${path} IS NOT NULL`;
  },

  jsonPathIsNull(column, pointer) {
    const path = toPostgresPath(pointer);
    // Type-based, not the `#>> path = 'null'` text comparison this used to
    // be: `#>>` renders a JSON null as SQL NULL (making the old form
    // three-valued, so `.pathIsNull()` silently missed stored JSON nulls) and
    // renders the JSON *string* "null" as the same text 'null' (falsely
    // matching it). jsonb_typeof distinguishes both, and COALESCE maps a
    // missing path to TRUE, keeping the predicate two-valued.
    return sql`COALESCE(jsonb_typeof(${column} #> ${path}) = 'null', TRUE)`;
  },

  jsonPathIsNumber(column, pointer) {
    const path = toPostgresPath(pointer);
    // jsonb_typeof returns NULL for a missing path; COALESCE keeps the
    // predicate two-valued so negations don't silently drop rows.
    return sql`COALESCE(jsonb_typeof(${column} #> ${path}) = 'number', FALSE)`;
  },

  jsonPathIsNotNull(column, pointer) {
    const path = toPostgresPath(pointer);
    // Mirror image of jsonPathIsNull; COALESCE maps a missing path to FALSE.
    return sql`COALESCE(jsonb_typeof(${column} #> ${path}) <> 'null', FALSE)`;
  },

  jsonScalarPathEquals(column, pointer, value) {
    const path = toPostgresPath(pointer);
    return sql`${column} #> ${path} = CAST(${JSON.stringify(value)} AS jsonb)`;
  },

  jsonSetProperties(column, patch, unsetProperties = []) {
    const withReplacements =
      Object.keys(patch).length === 0 ?
        column
      : sql`${column} || ${JSON.stringify(patch)}::jsonb`;
    if (unsetProperties.length === 0) return withReplacements;
    const properties = unsetProperties.map((property) => sql`${property}`);
    return sql`(${withReplacements}) - ARRAY[${sql.join(properties, sql`, `)}]::text[]`;
  },

  // ============================================================
  // Comparison Operations
  // ============================================================

  nullSafeEquals(left, right) {
    return sql`${left} IS NOT DISTINCT FROM ${right}`;
  },

  inList(left, values, negated) {
    const operator = negated ? sql.raw("NOT IN") : sql.raw("IN");
    const placeholders = values.map((value) => sql`${value}`);
    return sql`${left} ${operator} (${sql.join(placeholders, sql`, `)})`;
  },

  inListParameter(left, packedValues, { negated, elementType }) {
    const operator = negated ? sql.raw("NOT IN") : sql.raw("IN");
    // One placeholder for the whole list: the binding is JSON text, cast to
    // jsonb and expanded into a one-column relation, so arity never reaches
    // the SQL text. Elements arrive as text (`jsonb_array_elements_text`) and
    // are cast to whatever type the left operand was extracted as, mirroring
    // the jsonExtract* casts above.
    const element = sql`in_list_element.value${postgresElementCast(elementType)}`;
    return sql`${left} ${operator} (SELECT ${element} FROM jsonb_array_elements_text(${packedValues}::jsonb) AS in_list_element(value))`;
  },

  packListValue(values) {
    return packSqlListValue(values, "postgres");
  },

  // ============================================================
  // String Operations
  // ============================================================

  ilike(column, pattern) {
    // PostgreSQL has native ILIKE operator. Declaring backslash as the escape
    // character is a no-op (it is the LIKE default) but keeps the emitted SQL
    // identical in intent to SQLite, which has no default escape character.
    return sql`${column} ILIKE ${pattern} ${likeEscapeClause}`;
  },

  // ============================================================
  // Set Operations
  // ============================================================

  wrapSetOperationOperand(inner) {
    // PostgreSQL allows a complete SELECT (incl. its own WITH) as a
    // parenthesized compound operand.
    return sql`(${inner})`;
  },

  // ============================================================
  // Recursive CTE Path Operations
  // ============================================================

  textJsonArray: buildTextJsonArray,

  appendTextJsonArray(array, values) {
    return sql`(${array} || ${buildTextJsonArray(values)})`;
  },

  initializePath(nodeId) {
    // PostgreSQL uses text arrays: ARRAY[id]
    return sql`ARRAY[${nodeId}]`;
  },

  extendPath(currentPath, nodeId) {
    // Array concatenation: path || id
    return sql`${currentPath} || ${nodeId}`;
  },

  cycleCheck(nodeId, path) {
    // Check that id is NOT in array path
    // Returns TRUE if no cycle (id not in array)
    return sql`${nodeId} != ALL(${path})`;
  },

  // ============================================================
  // Value Binding & Literals
  // ============================================================

  bindValue(value) {
    return getSqlDialectProfile("postgres").bindValue(value);
  },

  unboundedLimit() {
    return sql.raw("ALL");
  },

  booleanLiteral(value) {
    return sql.raw(
      getSqlDialectProfile("postgres").booleanLiteralString(value),
    );
  },

  booleanLiteralString(value) {
    return getSqlDialectProfile("postgres").booleanLiteralString(value);
  },

  quoteIdentifier(name) {
    // PostgreSQL uses double quotes, escape embedded quotes by doubling
    return `"${name.replaceAll('"', '""')}"`;
  },

  // ============================================================
  // Vector Operations
  // ============================================================

  // Compile-time gate for `field.similarTo(...)`; the active
  // `VectorStrategy` (pgvector) owns the distance SQL.
  supportsVectors: true,

  // ============================================================
  // Fulltext Operations
  // ============================================================

  fulltext: tsvectorStrategy,
};
